import React from "react";
import { useGuestCheckoutSettings } from "../context/GuestCheckoutSettingsContext";

interface PaymentStatusMessageProps {
  readonly title: string;
  readonly message: string;
  readonly tone?: "info" | "error";
  readonly showSpinner?: boolean;
  readonly box?: "warning" | "error";
  readonly action?: React.ReactNode;
  readonly testId?: string;
}

/**
 * The interim states of the payment confirmation page (waiting for Collins,
 * payment received without booking detail, generic error). The widget renders
 * them in its own design — left-aligned heading and copy on the theme tokens,
 * see `.payment-status` in booking-form-mfe.scss — while the full app and the
 * other MFEs keep the centred legacy markup.
 */
const PaymentStatusMessage: React.FC<PaymentStatusMessageProps> = ({
  title,
  message,
  tone = "info",
  showSpinner = false,
  box,
  action,
  testId,
}) => {
  const { isWidget } = useGuestCheckoutSettings();
  const isError = tone === "error";

  if (isWidget) {
    return (
      <div
        className={`payment-confirm-page payment-status${isError ? " payment-status--error" : ""}`}
        data-testid={testId}
        aria-live={isError ? "assertive" : "polite"}
        role={box === "error" ? "alert" : undefined}
      >
        <h2 className="payment-title payment-status__title">{title}</h2>
        {box ? (
          <>
            <div className={`payment-status__box payment-status__box--${box}`}>
              <p className="payment-status__message">{message}</p>
            </div>
            {action && <div className="payment-status__action">{action}</div>}
          </>
        ) : (
          <>
            <p className="payment-status__message">{message}</p>
            {showSpinner && <div className="loading-spinner payment-status__spinner" aria-hidden="true"></div>}
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className={isError ? "klarna-confirm-page" : "payment-confirm-page"}
      style={{ textAlign: "center", marginTop: "4rem" }}
    >
      <h2>{title}</h2>
      <p style={isError ? { color: "red" } : undefined}>{message}</p>
      {showSpinner && (
        <div>
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default PaymentStatusMessage;
